import React from 'react'

import type { SharedProps } from './types'

import {
	useAnalyticsAverageClassHealth,
	useAnalyticsClassAwards,
} from '../../../context/provider'

import { HEALTH_COUNT_UP_DURATION, POINTS_PING_DURATION } from './durations'

import {
	EOMS_DARK_HIGHLIGHT,
	EOMS_BACKGROUND,
	EOMS_BORDER,
	EOMS_BORDER_RADIUS,
} from './styles'

import { AWARD_TYPES } from '@mission.io/mission-toolkit/constants'
import { useEffect } from 'react'
import { animated, useSpring, useSpringRef, useChain } from '@react-spring/web'
import { styled } from 'styled-components'
import { cssPlayerHeightUnit } from '../../../utility/constants'
import { StudentHealthIcon } from '../../../assets/icons'
import { theme } from '../../../theme'

/**
 * HealthAward - the health award page for end of mission sequence
 *
 * @param  {Object} props - the react props
 * @param  {started} props.started - true if the animation should or should have already started
 * @param  {() => void} props.onFinish - a call back for when the animations finishes
 *
 * @returns React$Node
 */
export function HealthAward({ started, onFinish }: SharedProps): React$Node {
	const healthAward = useAnalyticsClassAwards()?.find(
		award => award.type === AWARD_TYPES.CLASS_HEALTH
	)
	const averageClassHealth = useAnalyticsAverageClassHealth()
	useEffect(() => {
		if (healthAward == null || averageClassHealth == null) {
			onFinish()
		}
	}, [onFinish, healthAward, averageClassHealth])

	const countUpRef = useSpringRef()
	const { scoreProgress } = useSpring({
		scoreProgress: averageClassHealth ?? 0,
		from: { scoreProgress: 0 },
		reset: false,
		ref: countUpRef,
		config: { duration: HEALTH_COUNT_UP_DURATION, easing: easeOutQuadratic },
	})

	const pointRef = useSpringRef()
	const { pingProportion } = useSpring({
		from: {
			pingProportion: 0,
		},
		to: {
			pingProportion: 1,
		},
		reset: false,
		onRest: () => {
			if (started) {
				onFinish()
			}
		},
		ref: pointRef,
		config: { duration: POINTS_PING_DURATION },
	})

	useChain(started ? [countUpRef, pointRef] : [])

	if (averageClassHealth == null) {
		return null
	}

	const awardAmount = healthAward?.pointAmount ?? 0

	return (
		<CenterOnPage>
			<Container>
				<Header>Average Student Health</Header>
				<Line />
				<Header>
					<BarWrapper>
						<ImageWrapper>
							<img src={StudentHealthIcon} />
						</ImageWrapper>
						<Positioner>
							<animated.div
								style={{
									zIndex: 0,
									width: scoreProgress.interpolate(
										proportion => `${proportion}%`
									),
									height: '100%',
									backgroundColor: scoreProgress.interpolate(proportion =>
										getColor(proportion)
									),
									borderRadius: cssPlayerHeightUnit(2),
									position: 'absolute',
									boxShadow: pingProportion.interpolate(
										value =>
											`0px 0px 10px 10px ${getColor(
												averageClassHealth,
												(-Math.cos(6 * value * Math.PI) + 1) / 3
											)}`
									),
								}}
							/>
							<TextWrapper>
								<ProportionText
									style={{
										backgroundImage: scoreProgress.interpolate(
											proportion =>
												`linear-gradient(to right, ${EOMS_DARK_HIGHLIGHT} ${proportion}%, white ${proportion}%)`
										),
									}}>
									{scoreProgress.interpolate(
										proportion =>
											`${proportion.toLocaleString(undefined, {
												maximumFractionDigits: 0,
											})}%`
									)}
								</ProportionText>
							</TextWrapper>
							<PointBarWrapper>
								<ItemFlex>
									<ItemCircle />
									<ItemLine $proportion={0.5}>-100</ItemLine>
									<ItemCircle />
									<ItemLine $proportion={0.3}>+100</ItemLine>
									<ItemCircle />
									<ItemLine $proportion={0.14}>+200</ItemLine>
									<ItemCircle />
									<ItemLine $proportion={0.06}>+500</ItemLine>
									<ItemCircle />
								</ItemFlex>
							</PointBarWrapper>
						</Positioner>
					</BarWrapper>
				</Header>
				<animated.div
					style={{
						position: 'absolute',
						right: '0',
						top: pingProportion.interpolate(value =>
							cssPlayerHeightUnit(10 - 20 * easeOutQuadratic(value))
						),
						opacity: pingProportion.interpolate(value =>
							easeOutQuadratic(value)
						),
						fontSize: cssPlayerHeightUnit(5),
						color: awardAmount >= 0 ? '#99ff99' : theme.error,
					}}>
					<PointAmount>
						{awardAmount > 0 ? '+' : null}
						{awardAmount.toLocaleString(undefined, {
							maximumFractionDigits: 0,
						})}
					</PointAmount>
					<SmallText>Points Per Student</SmallText>
				</animated.div>
			</Container>
		</CenterOnPage>
	)
}

const COLORS = [
	{ minScore: 0, color: { r: 255.0, g: 0, b: 0 } },
	{ minScore: 15, color: { r: 255.0, g: 98.0, b: 23.0 } },
	{ minScore: 50, color: { r: 255.0, g: 224.0, b: 100.0 } },
	{ minScore: 85, color: { r: 176.0, g: 255.0, b: 100.0 } },
]

/**
 * getColor - get the color for the given score
 *
 * @param  {number} score - the score to get the color for
 * @param  {number=1} opacity? - the opacity the color should be
 *
 * @returns string - the rgba color associated with the score
 */
function getColor(score: number, opacity?: number = 1): string {
	let colorIndex = 0
	for (let i = 0; i < COLORS.length; i++) {
		if (COLORS[i].minScore > score) {
			break
		}
		colorIndex = i
	}

	if (colorIndex === COLORS.length - 1) {
		const color = COLORS[colorIndex].color
		return `rgba(${color.r}, ${color.g}, ${color.b}, ${opacity})`
	}

	let lowerColor = COLORS[colorIndex]
	let upperColor = COLORS[Math.min(COLORS.length - 1, colorIndex + 1)]

	let proportion = Math.max(
		(score - lowerColor.minScore) / (upperColor.minScore - lowerColor.minScore),
		0
	)

	const result = `rgba(${
		(upperColor.color.r - lowerColor.color.r) * proportion + lowerColor.color.r
	}, ${
		(upperColor.color.g - lowerColor.color.g) * proportion + lowerColor.color.g
	}, ${
		(upperColor.color.b - lowerColor.color.b) * proportion + lowerColor.color.b
	}, ${opacity})`

	return result
}

function easeOutQuadratic(proportion: number): number {
	return 1 - (1 - proportion) ** 2
}

const Container = styled.div`
	background-color: ${EOMS_BACKGROUND};
	border: ${EOMS_BORDER};
	border-radius: ${EOMS_BORDER_RADIUS};
	padding: 0px;
	position: relative;
	width: 90%;
`

const CenterOnPage = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	width: 100%;
	height: 100%;
`

const Header = styled.div`
	font-size: ${cssPlayerHeightUnit(5)};
	padding: ${cssPlayerHeightUnit(3.75)};
	text-align: center;
	border: ${EOMS_BORDER};
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`

const Line = styled.div`
	width: 100%;
	border-bottom: 1px solid ${EOMS_BORDER};
	border-radius: 0 0 ${EOMS_BORDER_RADIUS} ${EOMS_BORDER_RADIUS};
`
const POINT_BAR_HEIGHT = 10

const PointBarWrapper = styled.div`
	z-index: 2;
	width: 100%;
	position: relative;
	top: 120%;
	display: flex;
	flex-direction: row;
	gap: ${cssPlayerHeightUnit(2)};
	height: ${cssPlayerHeightUnit(POINT_BAR_HEIGHT)};
	padding-top: ${cssPlayerHeightUnit(1)};
`

const ImageWrapper = styled.div`
	flex: 1 0 ${cssPlayerHeightUnit(15)};
`

const BarWrapper = styled(PointBarWrapper)`
	height: ${cssPlayerHeightUnit(20)};
	padding: ${cssPlayerHeightUnit(2.5)};
	background-color: ${EOMS_DARK_HIGHLIGHT};
	border-radius: ${cssPlayerHeightUnit(10)} ${cssPlayerHeightUnit(2)}
		${cssPlayerHeightUnit(2)} ${cssPlayerHeightUnit(10)};
	margin-bottom: ${cssPlayerHeightUnit(POINT_BAR_HEIGHT / 2)};
`

const Positioner = styled.div`
	width: 100%;
	height: 100%;
	position: relative;
`

const TextWrapper = styled.div`
	z-index: 1;
	position: absolute;
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
`

const ItemLine = styled.div`
	margin-top: ${cssPlayerHeightUnit(0.6)};
	border-top: ${cssPlayerHeightUnit(0.75)} solid white;
	flex: ${({ $proportion }) => $proportion} ${({ $proportion }) => $proportion}
		0;
	padding-top: ${cssPlayerHeightUnit(1)};
	font-size: ${cssPlayerHeightUnit(3)};
`

/**
 * ItemCircle - a react component which puts a circle in the point line
 */
function ItemCircle() {
	return (
		<CirclePositioner>
			<Circle />
		</CirclePositioner>
	)
}

const CirclePositioner = styled.div`
	width: 0;
	max-width: 0;
	height: 0;
	max-height: 0;
	position: relative;
	flex: 0 0 0;
`

const CIRCLE_DIAMETER = 2

const Circle = styled.div`
	background-color: white;
	min-width: ${cssPlayerHeightUnit(CIRCLE_DIAMETER)};
	max-width: ${cssPlayerHeightUnit(CIRCLE_DIAMETER)};
	min-height: ${cssPlayerHeightUnit(CIRCLE_DIAMETER)};
	max-height: ${cssPlayerHeightUnit(CIRCLE_DIAMETER)};
	border-radius: ${cssPlayerHeightUnit(CIRCLE_DIAMETER)};
	left: ${cssPlayerHeightUnit(-CIRCLE_DIAMETER / 2)};
	position: relative;
`

const ItemFlex = styled.div`
	display: flex;
	width: 100%;
	height: 100%;
	padding: 0;
`

const ProportionText = styled(animated.div)`
	width: 100%;
	color: transparent;
	font-weight: bold;
	text-align: center;
	background-clip: text;
	text-fill-color: transparent;
	background-size: 100%;
	font-size: ${cssPlayerHeightUnit(8)};
	-webkit-background-clip: text;
	-moz-background-clip: text;
	-webkit-text-fill-color: transparent;
	-moz-text-fill-color: transparent;
`

const SmallText = styled.div`
	width: 100%;
	text-align: center;
	font-size: ${cssPlayerHeightUnit(2)};
	margin-top: ${cssPlayerHeightUnit(-1)};
`

const PointAmount = styled.div`
	margin-bottom: 0;
	padding-bottom: 0;
`
